/* eslint-disable data-test/onKeyDown */
import React, { useContext } from 'react';
import { FloatingFocusManager, FloatingPortal } from '@floating-ui/react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import DropdownComponent from './DropdownComponent';
import { Overlay } from '../../../Overlay';
import { ARROW_DOWN_KEY, ARROW_UP_KEY, TAB_KEYCODE } from '../../../Utils/constant';
import { DropdownContext } from '../../context';
import styles from '@dtsl/css-dropdown/dist/index.module.css';
function DropdownBody(props) {
  const {
    isOpen,
    isMobile,
    isOverlayOpen,
    setIsOpen,
    setIsOverlayOpen,
    dropdownControlRef,
    setSearchValue,
    dropdownBodyRef,
    focused,
    setFocused,
    floatingUiProps,
    dropdownWidth
  } = useContext(DropdownContext);
  const {
    menuClassName,
    isDropdownButton,
    inlineDropdownPanelStyles,
    panelDataTestId,
    inputStyles
  } = props;
  const {
    floatingStyles,
    refs,
    context
  } = floatingUiProps;
  const dropdownWidthStyle = dropdownWidth ? {
    width: dropdownWidth
  } : {};
  const menuClass = classNames({
    [styles['sib-dropdown-menu']]: true,
    [menuClassName]: !!menuClassName
  });
  function handleResize() {
    if (dropdownBodyRef.current) {
      if (isMobile && isOverlayOpen) {
        setIsOverlayOpen(false);
        setSearchValue('');
      } else {
        setIsOpen(false);
        setSearchValue('');
      }
      dropdownControlRef.current?.classList.remove(styles['sib-dropdown-control-focused']);
    }
  }
  React.useEffect(() => {
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);
  const handleNextOption = (e, firstOption, nextOption, lastOption, optionList) => {
    const selectedOptIndex = focused === lastOption ? firstOption : nextOption;
    Array.from(optionList).forEach(optionList => {
      if (optionList.classList?.contains('focused')) {
        optionList.classList.remove('focused');
        optionList.setAttribute('tabindex', '');
      }
    });
    setFocused(selectedOptIndex);
    optionList[selectedOptIndex]?.setAttribute('tabindex', '0');
    optionList[selectedOptIndex]?.classList.add('focused');
    optionList[selectedOptIndex]?.focus();
  };
  const handleKeyPress = event => {
    if (dropdownBodyRef && dropdownBodyRef.current) {
      let optionList = [];
      const isMultiSelect = dropdownBodyRef.current.children[0]?.classList?.contains(styles['sib-dropdown-multi-option']);
      const isCollapsibleList = dropdownBodyRef.current.children[1]?.classList?.contains('sib-collapsible');
      const isSectionList = dropdownBodyRef.current.children[0]?.classList?.contains(styles['sib-dropdown-option-section-header']);
      if (!isMultiSelect) {
        if (isCollapsibleList) {
          Array.from(dropdownBodyRef.current.children).forEach(group => {
            Array.from(group.children[1]?.children).forEach(groupChild => {
              if (!groupChild.classList?.contains(styles['locked'])) {
                optionList.push(groupChild); //Group Options
              }
            });
          });
        } else if (isSectionList) {
          Array.from(dropdownBodyRef.current.children).forEach(group => {
            if (group.children.length == 1) {
              Array.from(group.children[0]?.children).forEach(groupChild => {
                if (!groupChild.classList?.contains(styles['locked'])) {
                  optionList.push(groupChild); //Group Options
                }
              });
            } else {
              Array.from(group.children[1]?.children).forEach(groupChild => {
                if (!groupChild.classList?.contains(styles['locked'])) {
                  optionList.push(groupChild); //Group Options
                }
              });
            }
          });
        } else {
          Array.from(dropdownBodyRef.current.children).forEach(childrenItem => {
            if (!childrenItem.classList?.contains(styles['locked'])) {
              optionList.push(childrenItem); //Single Options
            }
          });
        }
        if (event.keyCode === TAB_KEYCODE && event.target.classList?.contains(styles['sib-dropdown-option-group-header'])) {
          //Set focus index while tabbing on collapsible element.
          Array.from(optionList).forEach((item, i) => {
            if (event.shiftKey) {
              const prevFocusedItem = event.target.parentNode.previousSibling.children[1];
              if (item.parentNode === prevFocusedItem) {
                setFocused(i - prevFocusedItem.children.length);
              }
            } else {
              if (item.parentNode === event.target.nextSibling) {
                setFocused(i);
              }
            }
          });
        }
        if (event.key === ARROW_UP_KEY && !event.target.classList?.contains(styles['sib-dropdown-menu'])) {
          const last = optionList.length - 1;
          const next = parseInt(focused) - 1;
          handleNextOption(event, last, next, 0, optionList);
        }
        if (event.key === ARROW_DOWN_KEY) {
          const first = 0;
          const next = focused === null ? 0 : parseInt(focused) + 1;
          handleNextOption(event, first, next, optionList.length - 1, optionList);
        }
      }
    }
  };
  if (isMobile) {
    return /*#__PURE__*/React.createElement(Overlay, {
      isOpen: isOverlayOpen,
      className: `${styles['sib-dropdown']} ${styles['dropdown-button']}`
    }, /*#__PURE__*/React.createElement("ul", {
      ref: dropdownBodyRef,
      className: styles['sib-dropdown-section-mobile-menu']
    }, /*#__PURE__*/React.createElement(DropdownComponent, props)));
  }
  return isOpen ? /*#__PURE__*/React.createElement(FloatingPortal, null, /*#__PURE__*/React.createElement(FloatingFocusManager, {
    context: context,
    initialFocus: -1,
    visuallyHiddenDismiss: true
  }, /*#__PURE__*/React.createElement("div", {
    ref: refs.setFloating,
    style: {
      ...floatingStyles
    }
  }, /*#__PURE__*/React.createElement("ul", {
    "data-testid": panelDataTestId ? panelDataTestId : null,
    ref: dropdownBodyRef,
    className: menuClass,
    "aria-expanded": "true",
    style: isDropdownButton ? {
      minWidth: 'max-content',
      ...dropdownWidthStyle,
      ...inputStyles,
      ...inlineDropdownPanelStyles
    } : {
      ...dropdownWidthStyle,
      ...inputStyles,
      ...inlineDropdownPanelStyles
    },
    role: "listbox",
    onKeyDown: handleKeyPress,
    tabIndex: "0",
    id: "sib-dropdown-menu-id",
    onFocus: () => {
      setFocused(null);
    }
  }, /*#__PURE__*/React.createElement(DropdownComponent, props))))) : null;
}
DropdownBody.defaultProps = {
  menuClassName: '',
  inlineDropdownPanelStyles: {},
  panelDataTestId: ''
};
DropdownBody.propTypes = {
  inlineDropdownPanelStyles: PropTypes.object,
  menuClassName: PropTypes.string,
  isDropdownButton: PropTypes.bool.isRequired,
  placement: PropTypes.string.isRequired,
  floatingStyles: PropTypes.object,
  refs: PropTypes.object,
  context: PropTypes.object,
  inputStyles: PropTypes.object,
  panelDataTestId: PropTypes.string
};
export default DropdownBody;